import { useFormReportsApi } from './useFormReportsApi';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useState } from 'react';

const observerColumnWidths = [
   22, // Submission Date
   8, // Vehicle
   20, // Date/Time
   18, // Observer
   18, // Location
   18, // Driver Name
   20, // Wear Company Badge
   25, // Wear Uniform as Per Policy
   17, // Sufficent Supplies
   27, // Perform Pre-Trip Vehicle Inspection
   9, // Fuel Full
   23, // Friendliness and Politeness
   25, // Handling Luggage with Care
   12, // Helpfulness
   9, // Patience
   20, // Wear Clean Uniform
   14, // Neatness
   14, // Punctuality
   14, // Team Player
   16, // Trip Awareness
   20, // Vehicle Cleanliness
   22, // Vehicle Coolant Level
   12, // Odor
   20, // Overall Cleanliness
   14, // Drives Safely
   40, // Practices safety when lifting cargo or luggage
   26, // Secures and locks vehicle at every stop
   37, // Onboarding/Offloading passengers safely
   26, // Remarks/Other Observations
];

export const useFormReports = () => {
   const { fetchObserverFormResponses } = useFormReportsApi();

   const [generatingReport, setGeneratingReport] = useState(false);

   // Shift all cells down by 2 rows to make space for title and headers
   const shiftSheet = (ws) => {
      const range = XLSX.utils.decode_range(ws['!ref']);
      const newWS = {};

      // Copy existing cells to new positions
      Object.keys(ws).forEach((cell) => {
         if (cell[0] === '!') return;

         const cellInfo = XLSX.utils.decode_cell(cell);

         // move only rows +2
         const newCell = XLSX.utils.encode_cell({
            r: cellInfo.r + 2,
            c: cellInfo.c,
         });

         newWS[newCell] = ws[cell];
      });

      // Update the range to reflect the shifted cells
      newWS['!ref'] = XLSX.utils.encode_range({
         s: { r: range.s.r + 2, c: range.s.c },
         e: { r: range.e.r + 2, c: range.e.c },
      });

      return newWS;
   };

   // Adjust column widths
   const setColumnWidths = (ws, widths) => {
      ws['!cols'] = widths.map((w) => ({ wch: w }));
   };

   const generateObserverFormResponsesReport = async (firstDay, lastDay) => {
      try {
         setGeneratingReport(true);

         const responses = await fetchObserverFormResponses(firstDay, lastDay);

         // Create base sheet from JSON
         let ws = XLSX.utils.json_to_sheet(responses);

         // Add two empty rows at the top
         ws = shiftSheet(ws);

         const wb = XLSX.utils.book_new();
         XLSX.utils.book_append_sheet(wb, ws, 'Observer Form Responses');

         // Set column widths
         setColumnWidths(ws, observerColumnWidths);

         // *** RECALCULATE RANGE AFTER shiftSheet ***
         let range = XLSX.utils.decode_range(ws['!ref']);

         // *** ADD TITLE IN A1 (OUTSIDE ORIGINAL RANGE) ***
         let message = `Observer Form Responses`;
         if (firstDay) {
            message += ` From ${firstDay}`;
         }
         if (lastDay) {
            message += ` To ${lastDay}`;
         }
         ws['A1'] = { v: message, s: { font: { sz: 14, bold: true } } };

         // Now EXPAND the range to include A1
         range.s.r = 0;
         range.s.c = 0;

         // headers are now in row 3
         const headerRow = 2;

         // *** FINAL MESSAGE ***
         const lastRow = range.e.r + 3;
         const totalRef = `A${lastRow}`;
         ws[totalRef] = { v: `Total form submissions: ${responses.length}` };

         // Expand range to include final message
         ws['!ref'] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: lastRow, c: range.e.c },
         });

         // Autofilter on headers (row 3)
         ws['!autofilter'] = {
            ref: XLSX.utils.encode_range({
               s: { r: headerRow, c: range.s.c },
               e: { r: headerRow, c: range.e.c },
            }),
         };

         // Download file
         const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
         const excelBlob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
         });

         let fileName = 'observer-form-responses';
         if (firstDay && lastDay) {
            fileName += `_${firstDay}-${lastDay}`;
         }

         saveAs(excelBlob, `${fileName}.xlsx`);
      } catch (error) {
         console.error(error);
         throw new Error('No data found');
      } finally {
         setGeneratingReport(false);
      }
   };

   return {
      generateObserverFormResponsesReport,
      generatingReport,
   };
};
